/* Litter-Robot content pages: video lightbox, in-view video playback, benefit slider. */
(() => {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Opens a YouTube video in a native <dialog>. The iframe loads only on open. */
  if (!customElements.get('lr-video-dialog')) {
    class LrVideoDialog extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('dialog');
        this.frame = this.querySelector('[data-lr-video-frame]');
        this.trigger = this.querySelector('[data-lr-video-open]');
        if (!this.dialog || !this.frame || !this.trigger) return;

        this.trigger.addEventListener('click', () => this.open());
        this.querySelector('[data-lr-video-close]')?.addEventListener('click', () => this.dialog.close());
        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.dialog.close();
        });
        this.dialog.addEventListener('close', () => {
          this.frame.replaceChildren();
          this.trigger.focus();
        });
      }

      open() {
        const id = this.dataset.youtubeId;
        if (!id) return;
        const iframe = document.createElement('iframe');
        iframe.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?autoplay=1&rel=0`;
        iframe.title = this.dataset.videoTitle || 'Video';
        iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
        iframe.allowFullscreen = true;
        this.frame.replaceChildren(iframe);
        this.dialog.showModal();
      }
    }
    customElements.define('lr-video-dialog', LrVideoDialog);
  }

  /* Plays a muted loop only while visible. With reduced motion it stays paused with controls. */
  if (!customElements.get('lr-inview-video')) {
    class LrInviewVideo extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector('video');
        if (!this.video) return;
        this.video.muted = true;
        this.video.loop = true;
        this.video.playsInline = true;

        if (prefersReducedMotion.matches) {
          this.video.controls = true;
          return;
        }

        this.observer = new IntersectionObserver(([entry]) => {
          if (entry.isIntersecting) {
            this.video.play().catch((error) => {
              // A pause() during scrolling aborts play(); only a real block needs the controls.
              if (error.name !== 'AbortError') this.video.controls = true;
            });
          } else {
            this.video.pause();
          }
        }, { threshold: 0.35 });
        this.observer.observe(this);
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }
    }
    customElements.define('lr-inview-video', LrInviewVideo);
  }

  /* Scroll-snap slider with prev/next arrows and dot navigation. */
  if (!customElements.get('lr-slider')) {
    class LrSlider extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-lr-track]');
        this.slides = Array.from(this.querySelectorAll('[data-lr-slide]'));
        this.prev = this.querySelector('[data-lr-prev]');
        this.next = this.querySelector('[data-lr-next]');
        this.dots = Array.from(this.querySelectorAll('[data-lr-dot]'));
        if (!this.track || this.slides.length === 0) return;

        this.prev?.addEventListener('click', () => this.goTo(this.current - 1));
        this.next?.addEventListener('click', () => this.goTo(this.current + 1));
        this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.goTo(index)));

        this.current = 0;
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.setCurrent(this.slides.indexOf(entry.target));
          });
        }, { root: this.track, threshold: 0.6 });
        this.slides.forEach((slide) => this.observer.observe(slide));
        this.setCurrent(0);
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }

      goTo(index) {
        const target = this.slides[Math.max(0, Math.min(index, this.slides.length - 1))];
        const offset = target.offsetLeft - (this.track.clientWidth - target.clientWidth) / 2;
        this.track.scrollTo({ left: offset, behavior: prefersReducedMotion.matches ? 'auto' : 'smooth' });
      }

      setCurrent(index) {
        if (index < 0) return;
        this.current = index;
        this.dots.forEach((dot, i) => dot.setAttribute('aria-current', i === index ? 'true' : 'false'));
        if (this.prev) this.prev.disabled = index === 0;
        if (this.next) this.next.disabled = index === this.slides.length - 1;
      }
    }
    customElements.define('lr-slider', LrSlider);
  }
})();
